import { CreateOrderRequest, OrderResponse } from '@/types/order';
import type { ApiProduct, BranchInfo, Product } from '@/types/product';

export interface ApiChannel {
  id: string;
  branchId: string;
  type: 'CHOWDECK' | 'STOREFRONT';
  name: string;
  email: string;
  phone_number: string;
  custom_url: string | null;
  default_url: string | null;
  missionStatement: string | null;
  headline: string | null;
  description: string | null;
  themeColor: string | null;
  premiseImages: string[];
}

export interface StorefrontChannel {
  id: string;
  branchId: string;
  name: string;
  email: string;
  phone_number: string;
  custom_url: string | null;
  missionStatement: string | null;
  headline: string | null;
  description: string | null;
  themeColor: string | null;
  premiseImages: string[];
}

export interface StockDetail {
  id: string;
  quantity: number;
  price: number;
  threshold: number | null;
  created_at: string;
  updated_at: string;
  zoho_product_id?: string | null;
  stock_update_id?: string | null;
  branch_id: string;
  product_Name?: string | null;
  brand_name: string;
  active_ingredient?: string | null;
  type?: string | null;
  dosage_form?: string | null;
  strength?: string | null;
  pack_size?: string | null;
  supplier_id?: string | null;
  class?: string | null;
  meta_data?: unknown;
  category?: string | null;
  sales?: number | null;
  image_url?: string | null;
  image_urls?: string[];
  branch: {
    id: string;
    name: string;
    lga: string;
    address: string;
    state: string;
    phone_number: string;
    dial_code: string;
  };
}

export interface AddFavoriteRequest {
  patientId: string;
  stockId: string;
}

export interface AddFavoriteResponse {
  data: {
    id: string;
    patient_id: string;
    stock_id: string;
    stock: {
      brand_name: string;
      active_ingredient: string;
      price: number;
    };
  };
}

// const API_BASE = "https://dispensaryapi.famasi.africa/api/v1";
const API_BASE = import.meta.env.VITE_BASE_URL;

type ErrorPayload = {
  error?: unknown;
  message?: unknown;
};

function extractErrorMessage(payload: unknown, fallback: string): string {
  if (payload && typeof payload === 'object') {
    const maybe = payload as ErrorPayload;
    if (typeof maybe.error === 'string' && maybe.error.trim())
      return maybe.error;
    if (typeof maybe.message === 'string' && maybe.message.trim())
      return maybe.message;
  }
  return fallback;
}

async function safeParseJson(res: Response): Promise<unknown> {
  try {
    return await res.json();
  } catch {
    return null;
  }
}

async function getJSON<T>(
  path: string,
  fetchOptions?: RequestInit
): Promise<T> {
  const controller = new AbortController();
  // const timeout = setTimeout(() => controller.abort(), 12_000);
  try {
    const init: RequestInit = {
      signal: controller.signal,
      ...(fetchOptions || {}),
    };
    const res = await fetch(`${API_BASE}${path}`, init);
    const parsed = await safeParseJson(res);
    if (!res.ok) {
      const message = extractErrorMessage(
        parsed,
        `Request failed with status ${res.status}`
      );
      throw new Error(message);
    }
    if (parsed === null) {
      throw new Error('Empty response');
    }
    return parsed as T;
  } catch (err: unknown) {
    if ((err as Error).name === 'AbortError') {
      throw new Error('Request timed out. Please try again.');
    }
    throw err;
  } finally {
    // clearTimeout(timeout);
  }
}

async function postJSON<Req, Res>(path: string, body: Req): Promise<Res> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 12_000);
  try {
    const res = await fetch(`${API_BASE}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
    const parsed = await safeParseJson(res);
    if (!res.ok) {
      const message = extractErrorMessage(
        parsed,
        `Request failed with status ${res.status}`
      );
      throw new Error(message);
    }
    if (parsed === null) {
      throw new Error('Empty response');
    }
    return parsed as Res;
  } catch (err: unknown) {
    if ((err as Error).name === 'AbortError') {
      throw new Error('Request timed out. Please try again.');
    }
    throw err;
  } finally {
    clearTimeout(timeout);
  }
}

export function mapApiProduct(apiProduct: ApiProduct): Product {
  const originalCategory = apiProduct.category ?? undefined;
  const categoryUpper = originalCategory?.toUpperCase();
  const prescription =
    categoryUpper === 'POM' ||
    apiProduct.class?.toLowerCase() === 'prescription' ||
    false;
  return {
    id: apiProduct.id,
    title: apiProduct.brand_name,
    price: apiProduct.price ?? 0,
    prescription,
    frequentlyBought: (apiProduct.sales ?? 0) > 5,
    category: originalCategory || 'Other',
    image_url: apiProduct.image_url ?? null,
    image_urls: apiProduct.image_urls || [],
    strength: apiProduct.strength || '',
    sales: apiProduct.sales ?? 0,
  };
}

export function mapStorefrontChannel(
  apiChannel: ApiChannel
): StorefrontChannel {
  return {
    id: apiChannel.id,
    branchId: apiChannel.branchId,
    name: apiChannel.name,
    email: apiChannel.email,
    phone_number: apiChannel.phone_number,
    custom_url: apiChannel.custom_url,
    missionStatement: apiChannel.missionStatement,
    headline: apiChannel.headline,
    description: apiChannel.description,
    themeColor: apiChannel.themeColor,
    premiseImages: apiChannel.premiseImages || [],
  };
}

type ApiEnvelope<T> = {
  success?: boolean;
  data: T;
  error?: string;
  message?: string;
  pagination?: {
    total: number;
    totalPages: number;
    page: number;
    limit: number;
  };
};

export type PaginatedResponse<T> = {
  data: T;
  pagination?: {
    total: number;
    totalPages: number;
    page: number;
    limit: number;
  };
};

export async function fetchBranchInfo(branchId: string): Promise<BranchInfo> {
  if (!branchId) throw new Error('Branch ID is required');
  const resp = await getJSON<ApiEnvelope<BranchInfo>>(`/branch/${branchId}`, {
    cache: 'no-store',
  });
  return resp.data;
}

export async function fetchProducts(
  branchId: string,
  page: number = 1,
  limit: number = 30
): Promise<PaginatedResponse<Product[]>> {
  if (!branchId) throw new Error('Branch ID is required');
  const resp = await getJSON<ApiEnvelope<Product[]>>(
    `/branch/${branchId}/get-stocks?page=${page}&limit=${limit}`,
    { cache: 'no-store' }
  );
  return { data: resp?.data || [], pagination: resp?.pagination };
}

export async function searchProducts(
  branchId: string,
  query: string,
  page: number = 1,
  limit: number = 100
): Promise<PaginatedResponse<Product[]>> {
  if (!branchId) throw new Error('Branch ID is required');
  const resp = await getJSON<ApiEnvelope<Product[]>>(
    `/branch/${branchId}/get-stocks?query=${encodeURIComponent(query)}&page=${page}&limit=${limit}`,
    { cache: 'no-store' }
  );
  return { data: resp?.data || [], pagination: resp?.pagination };
}

export async function fetchProductsByCategory(
  branchId: string,
  category: 'OTC' | 'POM',
  page: number = 1,
  limit: number = 100
): Promise<PaginatedResponse<Product[]>> {
  if (!branchId) throw new Error('Branch ID is required');
  const resp = await getJSON<ApiEnvelope<Product[]>>(
    `/branch/${branchId}/get-stocks?category=${category}&page=${page}&limit=${limit}`,
    { cache: 'no-store' }
  );
  return { data: resp?.data || [], pagination: resp?.pagination };
}

export interface StockCounts {
  pom: number;
  otc: number;
  others: number;
  total: number;
}

export async function fetchStockCounts(branchId: string): Promise<StockCounts> {
  if (!branchId) throw new Error('Branch ID is required');
  const resp = await getJSON<ApiEnvelope<StockCounts>>(
    `/branch/${branchId}/get-stock-counts`,
    { cache: 'no-store' }
  );
  return resp.data;
}

export async function fetchBranchChannels(
  branchId: string
): Promise<StorefrontChannel | null> {
  if (!branchId) throw new Error('Branch ID is required');
  const resp = await getJSON<ApiEnvelope<ApiChannel[]>>(
    `/branch/channels?branchId=${encodeURIComponent(branchId)}`,
    { cache: 'no-store' }
  );
  const channels = resp?.data || [];
  const storefrontChannel = channels.find(
    channel => channel.type === 'STOREFRONT'
  );
  return storefrontChannel ? mapStorefrontChannel(storefrontChannel) : null;
}

export async function fetchBranchChannelsByCustomUrl(
  customUrl: string
): Promise<StorefrontChannel | null> {
  if (!customUrl) throw new Error('customUrl is required');
  const resp = await getJSON<ApiEnvelope<ApiChannel[]>>(
    `/branch/channels?custom_url=${encodeURIComponent(customUrl)}`,
    { cache: 'no-store' }
  );
  const channels = resp?.data || [];
  const storefrontChannel = channels.find(
    channel => channel.type === 'STOREFRONT'
  );
  return storefrontChannel ? mapStorefrontChannel(storefrontChannel) : null;
}

export async function fetchStockDetail(stockId: string): Promise<StockDetail> {
  if (!stockId) throw new Error('Stock ID is required');
  const resp = await getJSON<ApiEnvelope<StockDetail>>(
    `/branch/stocks/${stockId}`
  );
  return resp.data;
}

export async function createOrder(
  orderData: CreateOrderRequest
): Promise<OrderResponse> {
  return await postJSON<CreateOrderRequest, OrderResponse>(
    '/orders',
    orderData
  );
}
